// src/pages/dashboard/account/AccountApiAccessTab.tsx
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2, Copy, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const ZAPIER_INVITE_LINK = "https://zapier.com/developer/public-invite/225265/8b2b7bea3aca41d7d49b01ee51840aa0/"; 

export default function AccountApiAccessTab() {
  // Use profile and loading directly from useAuth. loading indicates if auth (and initial profile) is ready.
  const { profile, user, queryClient, loading: authLoading } = useAuth(); 
  const [apiKey, setApiKey] = useState<string | null>(null);
  // isLoadingKey can be simplified or removed if authLoading and profile directly from context are sufficient
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);

  useEffect(() => {
    // When profile from AuthContext changes (e.g., after invalidation and refetch), update local apiKey state.
    if (profile) {
      setApiKey(profile.zapier_api_key || null);
    } else if (!authLoading && !user) { // If not loading auth and no user, clear API key
        setApiKey(null);
    }
  }, [profile, authLoading, user]);

  const handleGenerateKey = async () => {
    setIsGeneratingKey(true);
    try {
      const { data, error } = await supabase.functions.invoke('zapier-generate-key', {
        method: 'POST',
      });

      if (error) throw error;
      if (data?.apiKey) {
        // The API key is directly set in the DB.
        // Invalidate the profile query to refetch and update the UI.
        await queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
        toast.success('API Key generated successfully! Profile updated.');
        // setApiKey(data.apiKey); // This will be updated by the useEffect when profile refetches
      } else {
        throw new Error(data?.error || 'Failed to retrieve API key from response.');
      }
    } catch (error: any) {
      toast.error(`Failed to generate API key: ${error.message}`);
    } finally {
      setIsGeneratingKey(false);
    }
  };

  const copyToClipboard = () => {
    if (apiKey) {
      navigator.clipboard.writeText(apiKey)
        .then(() => toast.success('API Key copied to clipboard!'))
        .catch(() => toast.error('Failed to copy API Key.'));
    }
  };

  // Determine loading state for the API key section
  // authLoading implies profile might still be loading initially
  const isLoadingDisplayKey = authLoading && !profile; 

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-slate-gray">API Access & Zapier</h2>
        <p className="text-sm text-slate-gray mt-1">
          Manage your API key to connect ScheduleHero with other applications like Zapier.
        </p>
      </div>

      <div className="p-6 border rounded-lg bg-white shadow-sm">
        <h3 className="text-lg font-medium text-slate-gray mb-1">Your API Key</h3>
        <p className="text-sm text-slate-gray mb-4">
          Use this key to authenticate with the ScheduleHero API. Keep it secret!
        </p>
        
        {isLoadingDisplayKey ? (
          <div className="flex items-center space-x-2">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span className="text-slate-gray">Loading API Key...</span>
          </div>
        ) : apiKey ? (
          <div className="flex items-center space-x-2">
            <Input type="text" value={apiKey} readOnly className="font-mono bg-gray-50"/>
            <Button variant="outline" size="icon" onClick={copyToClipboard} aria-label="Copy API Key">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <p className="text-slate-gray">No API key generated yet. Click below to generate one.</p>
        )}

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button 
              variant={apiKey ? "outline" : "default"} 
              className="mt-4" 
              disabled={isGeneratingKey || isLoadingDisplayKey}
            >
              {(isGeneratingKey) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {apiKey ? 'Regenerate API Key' : 'Generate API Key'}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {apiKey ? 'Regenerate API Key?' : 'Generate API Key?'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {apiKey 
                  ? "This will invalidate your current API key. Any applications using the old key will stop working. Are you sure you want to continue?"
                  : "This will generate a new API key for your account."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isGeneratingKey}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleGenerateKey} disabled={isGeneratingKey}>
                {isGeneratingKey && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {apiKey ? 'Yes, Regenerate Key' : 'Yes, Generate Key'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
         <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-yellow-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Treat your API Key like a password. Do not share it publicly.
                </p>
              </div>
            </div>
          </div>
      </div>

      <div className="p-6 border rounded-lg bg-white shadow-sm">
        <h3 className="text-lg font-medium text-slate-gray mb-1">Connect with Zapier</h3>
        <p className="text-sm text-slate-gray mb-4">
          Automate your workflows by connecting ScheduleHero to thousands of apps with Zapier.
          You will need the API key from above to connect your ScheduleHero account in Zapier.
        </p>
        <Button 
            onClick={() => window.open(ZAPIER_INVITE_LINK, '_blank')}
            variant="default"
            disabled={!apiKey} // Optionally disable if no API key is generated
        >
          Connect ScheduleHero on Zapier
        </Button>
        <p className="text-xs text-slate-gray mt-2">
            You'll be taken to Zapier to accept the private invite for the ScheduleHero app.
        </p>
      </div>
    </div>
  );
}